import { type ReactNode, useRef } from 'react';
import {
  Header,
  type Key,
  ListBoxSection,
  type Selection
} from 'react-aria-components';
import {
  ListBox,
  ListBoxItem,
  ListBoxItemContentCompact
} from 'v5/design-system/ListBox';
import { SelectionCircle } from 'v5/design-system/SelectionCheck';
import { cn } from 'v5/platform/dom/cn';

const NO_IDS: ReadonlyArray<string> = [];

export interface SectionedChecklistItem {
  id: string;
  label: string;
  /** Short tags right after the label. */
  badges?: ReactNode;
  /** A short note at the end of the row. */
  extra?: ReactNode;
}

export interface SectionedChecklistSection {
  id: string;
  /** Without one, the section is just its items. */
  heading?: ReactNode;
  items: ReadonlyArray<SectionedChecklistItem>;
}

export interface SectionedChecklistProps {
  'aria-label': string;
  sections: ReadonlyArray<SectionedChecklistSection>;
  selected: ReadonlySet<string>;
  /**
   * The whole new set once per change, however many items it ticked or unticked (Shift for a
   * range): the items shown follow the list, the ones a search hides and the locked ones stay.
   */
  onChange: (selected: ReadonlySet<string>) => void;
  /** Items that can't be ticked now. */
  disabledIds?: ReadonlyArray<string>;
  /**
   * Items picked for good: a grey check and no picked background, still focusable, not
   * changeable; the item's `extra` says why.
   */
  lockedIds?: ReadonlyArray<string>;
  /** Shown when there are no items. */
  emptyState: ReactNode;
  className?: string;
}

/**
 * Items in titled sections, each ticked on its own: the design system's multi-select list. The
 * host filters the sections and places the search.
 */
export function SectionedChecklist({
  'aria-label': ariaLabel,
  sections,
  selected,
  onChange,
  disabledIds,
  lockedIds = NO_IDS,
  emptyState,
  className
}: SectionedChecklistProps) {
  const ticked = new Set([...selected, ...lockedIds]);
  // React Aria keeps where a Shift range starts on the selection it hands over: while the host
  // still ticks the same items, the list gets that one back, or a range loses its start
  const handedOver = useRef<Set<Key> | null>(null);
  const selectedKeys =
    handedOver.current !== null &&
    handedOver.current.size === ticked.size &&
    [...handedOver.current].every(key => ticked.has(String(key)))
      ? handedOver.current
      : ticked;
  const escaping = useRef(false);
  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all' || escaping.current) return;
    handedOver.current = keys;
    const next = new Set([...keys].map(String));
    const shown = sections
      .flatMap(section => section.items)
      .map(item => item.id)
      .filter(id => !lockedIds.includes(id));
    const changed = shown.filter(id => next.has(id) !== selected.has(id));
    if (changed.length === 0) return;
    onChange(
      new Set([
        ...[...selected].filter(id => !changed.includes(id)),
        ...changed.filter(id => next.has(id))
      ])
    );
  };
  return (
    // React Aria's list clears every tick on Escape; here Escape only closes what holds the list
    // (a popover, a dialog): the change it makes, in the same keydown, is not passed on
    <div
      className="contents"
      onKeyDownCapture={event => {
        if (event.key !== 'Escape') return;
        escaping.current = true;
        queueMicrotask(() => {
          escaping.current = false;
        });
      }}
    >
      <ListBox
        aria-label={ariaLabel}
        className={cn('divide-y-0', className)}
        selectionMode="multiple"
        selectionBehavior="toggle"
        selectedKeys={selectedKeys}
        disabledKeys={disabledIds}
        onSelectionChange={handleSelectionChange}
        renderEmptyState={() => (
          <div className="p-6 text-center text-sm text-fg-muted">
            {emptyState}
          </div>
        )}
      >
        {sections.map(section => (
          <ListBoxSection
            key={section.id}
            // grouped as the column picker's groups read
            // rows inset from the section's lines, so a ticked row's grey never meets them
            className="flex flex-col gap-0.5 border-t border-border-default px-1.5 py-1.5 first:border-t-0"
          >
            {section.heading && (
              <Header className="px-1.5 pt-1 pb-0.5 text-sm text-fg-muted">
                {section.heading}
              </Header>
            )}
            {section.items.map(item => {
              const isLocked = lockedIds.includes(item.id);
              return (
                <ListBoxItem
                  key={item.id}
                  id={item.id}
                  textValue={item.label}
                  ref={element => {
                    // React Aria keeps aria-disabled for unfocusable options; a locked one is both
                    // focusable and not changeable. Its own disabled rows keep theirs.
                    if (
                      !(element instanceof HTMLElement) ||
                      element.hasAttribute('data-disabled')
                    )
                      return;
                    if (isLocked) element.setAttribute('aria-disabled', 'true');
                    else element.removeAttribute('aria-disabled');
                  }}
                  className={cn(
                    'gap-3 rounded-md px-1.5 py-1.5 last:rounded-md data-disabled:cursor-not-allowed data-disabled:opacity-50',
                    isLocked &&
                      'cursor-default hover:bg-transparent aria-selected:bg-transparent'
                  )}
                >
                  <ListBoxItemContentCompact
                    primary={
                      item.badges ? (
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate">{item.label}</span>
                          {item.badges}
                        </span>
                      ) : (
                        item.label
                      )
                    }
                    showCheckmark={!isLocked}
                    extra={
                      isLocked ? (
                        <span className="flex items-center gap-3">
                          {item.extra && (
                            <span className="text-sm text-fg-muted">
                              {item.extra}
                            </span>
                          )}
                          <SelectionCircle locked />
                        </span>
                      ) : (
                        item.extra && (
                          <span className="text-sm text-fg-muted">
                            {item.extra}
                          </span>
                        )
                      )
                    }
                  />
                </ListBoxItem>
              );
            })}
          </ListBoxSection>
        ))}
      </ListBox>
    </div>
  );
}
